import { ReactFlow, Background, Controls, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useEffect, useMemo, useRef, useState } from 'react';
type MapContent = {
  nodes: Array<{ id: string; label: string; notes: string }>;
  edges: Array<{ from: string; to: string }>;
};
export function StudyMindMap({ map }: { map: MapContent }) {
  const container = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const measure = () =>
      setColumns(element.clientWidth >= 720 ? 3 : element.clientWidth >= 440 ? 2 : 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const [view, setView] = useState<'map' | 'list'>('map');
  const nodes = useMemo(
    () =>
      map.nodes.map((n, i) => ({
        id: n.id,
        data: { label: n.label },
        position: { x: (i % columns) * 240, y: Math.floor(i / columns) * 140 },
        className: '!rounded-2xl !border-hairline !bg-canvas !text-ink !font-bold !p-4',
        style: { width: 200 },
        draggable: false,
        connectable: false,
      })),
    [map.nodes, columns],
  );
  const edges = useMemo(
    () =>
      map.edges.map((e, i) => ({
        id: `edge-${i}`,
        source: e.from,
        target: e.to,
        markerEnd: { type: MarkerType.ArrowClosed },
        animated: false,
      })),
    [map.edges],
  );
  const labels = new Map(map.nodes.map((n) => [n.id, n.label]));
  return (
    <div ref={container} className="mt-4">
      <div className="mb-3 flex gap-2" aria-label="Mind map view">
        <button
          type="button"
          className="btn-pill-ghost"
          aria-pressed={view === 'map'}
          onClick={() => setView('map')}
        >
          Map
        </button>
        <button
          type="button"
          className="btn-pill-ghost"
          aria-pressed={view === 'list'}
          onClick={() => setView('list')}
        >
          Text list
        </button>
      </div>
      {view === 'map' ? (
        <div
          className="h-80 min-w-0 overflow-hidden rounded-2xl border border-hairline bg-canvas-pure"
          aria-label="Concept relationship map"
        >
          <ReactFlow
            key={columns}
            nodes={nodes}
            edges={edges}
            fitView
            fitViewOptions={{ padding: 0.12, minZoom: 0.75, maxZoom: 1.1 }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnScroll={false}
            preventScrolling={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
      ) : (
        <div className="rounded-2xl bg-canvas-pure p-4">
          <ul className="space-y-3">
            {map.nodes.map((n) => (
              <li key={n.id}>
                <h4 className="font-bold text-ink">{n.label}</h4>
                <p className="mt-1 whitespace-pre-wrap text-sm text-ink-soft">{n.notes}</p>
              </li>
            ))}
          </ul>
          <h4 className="mt-5 font-bold text-ink">Suggested connections</h4>
          <ul className="mt-2 space-y-2 text-sm">
            {map.edges.map((e, i) => (
              <li key={i}>
                {labels.get(e.from)} → {labels.get(e.to)}
              </li>
            ))}
          </ul>
          {map.edges.length === 0 && <p className="mt-2 text-sm">No connections added yet.</p>}
        </div>
      )}
    </div>
  );
}
